import { useCreateDialogForm, useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import { useCreateDraft } from 'components/create-dialog/useCreateDraft';
import { DraftSaveMode } from 'components/drafts/DraftSaveMode';
import ActionHeader from 'components/forms/ActionHeader';
import FormWrapper from 'components/forms/form-components/FormWrapper';
import Loader from 'components/Loader';
import Section from 'components/section-menu/Section';
import { HighlightedFieldsProvider, SectionActionsProvider } from 'components/section-menu/SectionActionsContext';
import SectionLayout from 'components/section-menu/SectionLayout';
import { useSectionErrors } from 'components/section-menu/useSectionErrors';
import BasicTable from 'components/tables/BasicTable';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { useMemo, useRef } from 'react';
import { Control, FieldValues, FormProvider } from 'react-hook-form';
import { Link as RouterLink } from 'react-router-dom';
import { useAddTeamMutation, useUpdateTeamMutation } from 'store/api/teams';
import { ClassifierData } from 'types/classifiers';
import { Team, TeamEmployee, TeamForm } from 'types/teams';
import { errorAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { formatEmployeeName, useLastNameFirst } from 'utils/nameOrder';
import { useGoBack, useLeaveForm, useReturnToLocation } from 'utils/useNavigateWithReturnState';

import CancelButton from 'components/buttons/CancelButton';
import PermissionButton from 'components/buttons/PermissionButton';
import SaveButton from 'components/buttons/SaveButton';
import CustomFields from 'components/forms/inputs/custom-fields/CustomFields';
import InputWrapper from 'components/forms/inputs/rhf-inputs/InputWrapper';
import ReadonlyField from 'components/forms/inputs/rhf-other/ReadonlyField';
import ConfirmNavigation from 'components/navigation/ConfirmNavigation';
import useFocusedEditMode from 'hooks/useFocusedEditMode';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'store';
import { CustomField } from 'types/customFields';
import { TeamActiveField, TeamContactFields, TeamFields } from './form-sections/TeamFields';
import { useTeamForm } from './form-sections/useTeamForm';
import { teamFormToRequest } from './teamFormMapping';

const FIELD_TO_SECTION_MAP: Record<string, string> = {
    teamType: 'matters.general',
    dmsTeam: 'matters.general',
    name: 'matters.general',
    abbreviation: 'matters.general',
    isParent: 'matters.general',
    parentTeam: 'matters.general',
    teamPracticeAreas: 'matters.general',
    teamBusinessAreas: 'matters.general',
    teamOffices: 'matters.general',
    emailAddress: 'matters.general',
    isActive: 'matters.general',
};

type Props = {
    type: 'add' | 'edit';
    initialData?: TeamForm;
    classifiers: Record<string, ClassifierData>;
    refetch?: () => void;
    entityCustomFields?: CustomField[];
    team?: Team;
};

const buildTeamEmployeesColumns = (lastNameFirst: boolean): DataTableColumnDef<TeamEmployee>[] => [
    {
        header: t('employees.employee'),
        accessorKey: 'name',
        cell: ({ row }) => (
            <RouterLink
                className="entity-link text-fg hover:text-link no-underline hover:underline"
                to={`/employee/${row.original.id}`}
            >
                {formatEmployeeName(row.original, lastNameFirst) || '-'}
            </RouterLink>
        ),
    },
    {
        header: t('office'),
        id: 'office.name',
        accessorFn: (row) => row.office?.name,
    },
];

const TeamEmployeesTable = ({ initialData }: { initialData?: TeamForm }) => {
    const lastNameFirst = useLastNameFirst();
    const coreEmployees = useMemo(
        () => (initialData?.readonlyTeamEmployees || []).filter((employee) => employee.isCoreEmployee),
        [initialData]
    );
    const columns = useMemo(() => buildTeamEmployeesColumns(lastNameFirst), [lastNameFirst]);

    if (coreEmployees.length === 0) return null;

    return (
        <div className="mx-[20%] mt-4">
            <BasicTable data={coreEmployees} striped title={t('teams.team-employees')} columns={columns} />
        </div>
    );
};

const TeamPageForm = ({
    type = 'add',
    initialData,
    classifiers,
    refetch,
    entityCustomFields,
    team,
}: Props): JSX.Element => {
    useFocusedEditMode('teams');
    const { id: teamId } = useCreateRoute().params;
    const returnTo = useReturnToLocation(teamId ? `/team/${teamId}` : '/teams');
    const goBack = useGoBack(returnTo);
    const leaveForm = useLeaveForm();
    const lastNameFirst = useLastNameFirst();
    const form = useTeamForm({ initialData, classifiers, entityCustomFields, team });
    const { methods, customFields, mapCustomFieldsToDto, hasBackOfficeSettingsPermission } = form;
    useCreateDialogForm(methods);
    const toRequest = (formData: TeamForm) =>
        teamFormToRequest(formData, {
            customFields: mapCustomFieldsToDto(formData as unknown as Record<string, unknown>),
            canEditDmsTeam: hasBackOfficeSettingsPermission,
            initialForm: initialData,
        });
    const draft = useCreateDraft({ record: 'team', methods, toPayload: toRequest });

    const { handleSubmit, control, reset, formState } = methods;
    const sectionErrors = useSectionErrors(formState.errors, FIELD_TO_SECTION_MAP);
    const { buttonsDisabled } = useSelector((state) => state.buttons);

    const [addTeam] = useAddTeamMutation();
    const [updateTeam] = useUpdateTeamMutation();

    const bypassNavigationBlockerRef = useRef(false);

    const onSubmit = async (formData: TeamForm, action: 'create' | 'update'): Promise<void> => {
        try {
            const data = toRequest(formData);
            if (action === 'create') {
                // A draft is created by completing it: the team keeps the draft's id.
                await (draft.draftId === null ? addTeam(data).unwrap() : draft.complete(data))
                    .then((resp) => {
                        bypassNavigationBlockerRef.current = true;
                        reset({}, { keepValues: true });
                        leaveForm(`/team/${resp.id}`);
                    })
                    .catch(console.error);
            } else if (action === 'update' && teamId && !isNaN(Number(teamId))) {
                await updateTeam({
                    ...data,
                    id: teamId,
                })
                    .unwrap()
                    .then(() => {
                        bypassNavigationBlockerRef.current = true;
                        if (refetch) refetch();
                        reset({}, { keepValues: true });
                        leaveForm(`/team/${teamId}`);
                    })
                    .catch(console.error);
            }
        } catch (e) {
            console.error('onSubmit error: ', e);
            errorAlert(t('forms.error-submitting-the-form'));
        }
    };

    const submitCreate = () => {
        handleSubmit((data) => onSubmit(data, 'create'))();
    };

    const submitUpdate = () => {
        handleSubmit((data) => onSubmit(data, 'update'))();
    };

    if (form.isLoading) {
        return <Loader />;
    }

    // Show inital name if is edit form. Dont do dynamically to prevent unneeded rerenders
    const getFormTitle = () => {
        switch (type) {
            case 'edit':
                return initialData?.name;
            default:
                return t('teams.add-new-team');
        }
    };

    const getFormHeader = () => {
        const actions: JSX.Element[] = [<CancelButton key="cancel" onClick={goBack} />];

        if (type === 'add') {
            actions.push(
                ...(draft.button ? [draft.button] : []),
                <SaveButton
                    key="addSave"
                    permissions={['CREATE.TEAMS']}
                    onClick={submitCreate}
                    disabled={buttonsDisabled}
                />
            );
        } else if (type === 'edit' && teamId) {
            actions.push(
                <PermissionButton
                    key="editSave"
                    permissions={['UPDATE.TEAMS']}
                    color="secondary"
                    variant="contained"
                    size="small"
                    onClick={submitUpdate}
                    disabled={buttonsDisabled}
                >
                    <FormattedMessage id="save" />
                </PermissionButton>
            );
        }

        return <ActionHeader title={getFormTitle()} actions={actions} onBack={goBack} draft={draft.detail} />;
    };

    return (
        <FormProvider {...methods}>
            <SectionActionsProvider>
                <HighlightedFieldsProvider>
                    <FormWrapper>
                        {getFormHeader()}
                        <DraftSaveMode mode={draft.mode}>
                            <SectionLayout sectionErrors={sectionErrors}>
                                <Section title="matters.general">
                                    <TeamFields form={form} />
                                    <TeamContactFields form={form} />
                                    <TeamActiveField form={form} />
                                    <InputWrapper
                                        name="chairmen"
                                        label="teams.team-chairmen"
                                        readonly={true}
                                        hasErrors={false}
                                    >
                                        <ReadonlyField
                                            value={(initialData?.readonlyTeamEmployees ?? [])
                                                .filter((employee) => employee.teamMembershipType === 'CHAIRMAN')
                                                .map((employee) => formatEmployeeName(employee, lastNameFirst) || '-')}
                                        />
                                    </InputWrapper>
                                    <InputWrapper
                                        name="supports"
                                        label="teams.team-supports"
                                        readonly={true}
                                        hasErrors={false}
                                    >
                                        <ReadonlyField
                                            value={(initialData?.readonlyTeamEmployees ?? [])
                                                .filter((employee) => employee.teamMembershipType === 'SUPPORT')
                                                .map((employee) => formatEmployeeName(employee, lastNameFirst) || '-')}
                                        />
                                    </InputWrapper>
                                </Section>
                                {customFields?.length > 0 ? (
                                    <CustomFields
                                        control={control as unknown as Control<FieldValues>}
                                        fields={customFields}
                                    />
                                ) : null}
                            </SectionLayout>
                        </DraftSaveMode>
                        <TeamEmployeesTable initialData={initialData} />
                    </FormWrapper>
                    <ConfirmNavigation
                        control={control}
                        bypassRef={bypassNavigationBlockerRef}
                        draft={type === 'add' ? draft.leave : null}
                    />
                </HighlightedFieldsProvider>
            </SectionActionsProvider>
        </FormProvider>
    );
};

export default TeamPageForm;
